import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCollection } from "@awsui/collection-hooks";
import Button from "@awsui/components-react/button";
import EmptyState from "components/EmptyState";
import { CollectionPreferencesProps } from "@awsui/components-react/collection-preferences/interfaces";


interface UseTableOptions {
    emptyStateSubtitle: Error;
    emptyStateTitle: string;
    pageSize?: number;
}

/* eslint-disable  @typescript-eslint/no-explicit-any */
const useTable = (allItems: Array<any>, options: UseTableOptions): any => {
    const { t } = useTranslation("common");

    const pageSizePreference = {
        title: t("table.pageSizeTitle"),
        options: [
            { value: 10, label: t("table.pageSizeOption", { count: 10 }) },
            { value: 20, label: t("table.pageSizeOption", { count: 20 }) },
            { value: 50, label: t("table.pageSizeOption", { count: 50 }) },
            { value: 200, label: t("table.pageSizeOption", { count: 200 }) }
        ]
    };

    const { emptyStateTitle, emptyStateSubtitle, pageSize } = options;

    const collectionPreferencesProps = {
        pageSizePreference,
        cancelLabel: t("table.cancel"),
        confirmLabel: t("table.confirm"),
        title: t("table.preferences")
    };

    const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences<any>>({
        pageSize: pageSize || 50
    });
    const {
        items,
        actions,
        filteredItemsCount,
        collectionProps,
        filterProps,
        paginationProps
    } = useCollection(allItems, {
        filtering: {
            empty: (
                <EmptyState
                    title={emptyStateTitle}
                    subtitle={emptyStateSubtitle?.message}
                />
            ),
            noMatch: (
                <EmptyState
                    title={emptyStateTitle}
                    subtitle={t("table.noMatch")}
                    action={
                        <Button onClick={() => actions.setFiltering("")}>
                            {t("buttons.clearFilter")}
                        </Button>
                    }
                />
            )
        },
        pagination: {
            pageSize: preferences.pageSize
        },
        sorting: {},
        selection: {
            keepSelection: true
        },
    });
    const { selectedItems } = collectionProps;

    return {
        preferences,
        setPreferences,
        items,
        actions,
        filteredItemsCount,
        collectionProps,
        filterProps,
        paginationProps,
        selectedItems,
        collectionPreferencesProps
    };
};

export default useTable;
